import React from 'react';
import cx from 'classnames';

import { ContentEditable as Base } from '@lexical/react/LexicalContentEditable';

import * as styles from './ContentEditable.css';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';

const TRANSPARENT_BACKGROUND = 'rgba(0, 0, 0, 0)';
const DEFAULT_BACKGROUND = '#FFFFFF';

/**
 * Attempts to find an ancestor of the provided element that has a background color set
 */
function findAncestorBackgroundColor(htmlElement: HTMLElement): string {
  const style = getComputedStyle(htmlElement);
  if (style.backgroundColor && style.backgroundColor !== TRANSPARENT_BACKGROUND) {
    return style.backgroundColor;
  }
  if (htmlElement.tagName === 'BODY') {
    return DEFAULT_BACKGROUND;
  }
  const parent = htmlElement.parentElement;
  if (parent) {
    return findAncestorBackgroundColor(parent);
  }
  return DEFAULT_BACKGROUND;
}

function applyInvertedTextColor(colorToInvert: string, htmlElement: HTMLElement) {
  // eslint-disable-next-line no-param-reassign
  htmlElement.style.color = colorToInvert;
  // eslint-disable-next-line no-param-reassign
  htmlElement.style.filter = 'brightness(0.5) invert(0.75)';
}

export function ContentEditable({
  placeholder = 'Enter text',
  isPlaceholderDefaultHidden = false,
  className,
  ...props
}: Omit<React.ComponentProps<typeof Base>, 'placeholder'> & {
  placeholder?: string;
  isPlaceholderDefaultHidden?: boolean;
}) {
  const [placeholderShown, setPlaceholderShown] = React.useState<boolean>(false);

  const [isFocused, setIsFocused] = React.useState<boolean>(false);
  const [editor] = useLexicalComposerContext();

  React.useEffect(() => {
    const handleFocus = () => setIsFocused(true);
    const handleBlur = () => setIsFocused(false);

    editor.registerRootListener((rootElement) => {
      if (rootElement) {
        rootElement.addEventListener('focus', handleFocus);
        rootElement.addEventListener('blur', handleBlur);
      }
      return () => {
        if (rootElement) {
          rootElement.removeEventListener('focus', handleFocus);
          rootElement.removeEventListener('blur', handleBlur);
        }
      };
    });
  }, [editor]);

  const placeholderRef = React.useCallback((ref: HTMLElement | null) => {
    setPlaceholderShown(!!ref);
    // There are scenarios where the current text color matches the background color in which case
    // our placeholder text will not be visible in the document. Here we set the text color of our
    // placeholder to an opposite color of the closest background color.
    if (ref && ref.parentElement) {
      applyInvertedTextColor(findAncestorBackgroundColor(ref.parentElement), ref);
    }
  }, []);

  return (
    <div className={cx(styles.contentEditableContainer, className)}>
      <Base
        {...props}
        className={styles.contentEditable}
        {...(placeholder && !isFocused
          ? ({
              'aria-placeholder': placeholder,
              placeholder: (
                <div ref={placeholderRef} className={styles.placeholder({ defaultHidden: isPlaceholderDefaultHidden })}>
                  {placeholder}
                </div>
              ),
            } as any)
          : {})}
        // ensures the input matches the width of the placeholder.
        style={placeholderShown ? { position: 'absolute', height: '100%' } : undefined}
      />
    </div>
  );
}
